import { useState, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Globe
} from 'lucide-react';

// Currency flags mapping
const currencyFlags = {
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  JPY: '🇯🇵',
  AUD: '🇦🇺',
  CAD: '🇨🇦',
  CHF: '🇨🇭',
  CNY: '🇨🇳',
  NZD: '🇳🇿'
};

const impactColors = {
  High: { bg: 'bg-red-500/20', text: 'text-red-400', dot: 'bg-red-500' },
  Medium: { bg: 'bg-amber-500/20', text: 'text-amber-400', dot: 'bg-amber-500' },
  Low: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', dot: 'bg-emerald-500' },
  Holiday: { bg: 'bg-slate-500/20', text: 'text-slate-400', dot: 'bg-slate-500' }
};

// Fallback economic events when API fails
const FALLBACK_EVENTS = [
  { currency: 'USD', event: 'Initial Jobless Claims', impact: 'High', time: '08:30', previous: '219K', forecast: '222K' },
  { currency: 'USD', event: 'Continuing Jobless Claims', impact: 'Medium', time: '08:30', previous: '1.875M', forecast: '1.880M' },
  { currency: 'EUR', event: 'ECB Monetary Policy Meeting Accounts', impact: 'High', time: '07:30', previous: '-', forecast: '-' },
  { currency: 'GBP', event: 'BoE MPC Meeting Minutes', impact: 'High', time: '09:30', previous: '-', forecast: '-' },
  { currency: 'USD', event: 'Philadelphia Fed Manufacturing Index', impact: 'Medium', time: '08:30', previous: '4.5', forecast: '5.0' },
  { currency: 'EUR', event: 'Consumer Confidence', impact: 'Medium', time: '10:00', previous: '-14.0', forecast: '-13.5' },
  { currency: 'JPY', event: 'BoJ Interest Rate Decision', impact: 'High', time: '03:00', previous: '0.25%', forecast: '0.25%' },
  { currency: 'USD', event: 'Existing Home Sales', impact: 'Medium', time: '10:00', previous: '4.04M', forecast: '4.10M' }
];

export default function EconomicCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [impactFilter, setImpactFilter] = useState('all');

  useEffect(() => {
    fetchCalendarData();
  }, []);

  const fetchCalendarData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Try FairEconomy API first
      const response = await axios.get('https://nfs.faireconomy.media/ff_calendar_thisweek.json', {
        timeout: 8000
      });
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const transformedEvents = response.data.map((event, idx) => ({
          id: `event-${idx}`,
          date: new Date(event.date),
          time: event.date ? new Date(event.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-',
          currency: event.country || 'USD',
          event: event.title || 'Unknown Event',
          impact: event.impact === 'Holiday' ? 'Holiday' : event.impact || 'Low',
          previous: event.previous || '-',
          forecast: event.forecast || '-',
          actual: event.actual || null
        }));
        
        setEvents(transformedEvents);
        setError(null);
      } else {
        throw new Error('No data received');
      }
    } catch (err) {
      console.error('Calendar API error:', err.message);
      
      // Use fallback data
      const today = new Date();
      const fallbackWithDates = FALLBACK_EVENTS.map((event, idx) => ({
        id: `fallback-${idx}`,
        date: today,
        ...event
      }));
      
      setEvents(fallbackWithDates);
      setError('Using cached calendar data');
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      const selected = new Date(selectedDate);
      const sameDay = eventDate.toDateString() === selected.toDateString();
      const matchesImpact = impactFilter === 'all' || event.impact === impactFilter;
      return sameDay && matchesImpact;
    });
  }, [events, selectedDate, impactFilter]);

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  return (
    <Card className="bg-[#0D131C] border-[#1E293B] overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-[#1E293B] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
            <CalendarIcon className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-white font-['Manrope']">Economic Calendar</h3>
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px] animate-pulse">LIVE</Badge>
            </div>
            <p className="text-xs text-slate-500">High-impact market moving events</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={fetchCalendarData}
          disabled={loading}
          className="text-slate-400 hover:text-white"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="p-4 space-y-4">
        {/* Date Navigation */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigateDate(-1)}
            className="text-slate-400 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-center">
            <p className="font-bold text-white">{formatDate(selectedDate)}</p>
            {isToday(selectedDate) && (
              <Badge className="bg-cyan-500/20 text-cyan-400 text-[10px] mt-1">Today</Badge>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigateDate(1)}
            className="text-slate-400 hover:text-white"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Impact Filter */}
        <div className="flex items-center gap-2 justify-center">
          <Globe className="w-4 h-4 text-slate-500" />
          {['all', 'High', 'Medium', 'Low'].map((filter) => (
            <Button
              key={filter}
              variant={impactFilter === filter ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setImpactFilter(filter)}
              className={`text-xs ${
                impactFilter === filter 
                  ? 'bg-[#152A4A] text-cyan-400' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {filter === 'all' ? 'All Impact' : filter}
            </Button>
          ))}
        </div>

        {/* Events List */}
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Loading events...</p>
          </div>
        ) : error ? (
          <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-lg p-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#FFCC00]" />
            <span className="text-[#FFCC00] text-sm">{error}</span>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon className="w-12 h-12 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-400">No events scheduled for this date</p>
            <p className="text-slate-500 text-xs mt-1">Try selecting a different date or filter</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {filteredEvents.map((event) => {
              const impact = impactColors[event.impact] || impactColors.Low;
              return (
                <div 
                  key={event.id}
                  className="bg-[#0A0E14] rounded-lg p-3 border border-[#1E293B] hover:border-[#2D3748] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="text-center min-w-[50px]">
                        <span className="text-lg">{currencyFlags[event.currency] || '🌐'}</span>
                        <p className="text-[10px] text-slate-500 font-mono">{event.currency}</p>
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{event.event}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`${impact.bg} ${impact.text} text-[10px]`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${impact.dot} mr-1`} />
                            {event.impact}
                          </Badge>
                          <span className="text-[10px] text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {event.time}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-[10px]">
                      <p className="text-slate-500">Previous: <span className="text-slate-300">{event.previous}</span></p>
                      <p className="text-slate-500">Forecast: <span className="text-cyan-400">{event.forecast}</span></p>
                      {event.actual && (
                        <p className="text-slate-500">Actual: <span className="text-[#00E676]">{event.actual}</span></p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 pt-2 border-t border-[#1E293B]">
          <span className="flex items-center gap-1 text-[10px] text-slate-500">
            <span className="w-2 h-2 rounded-full bg-red-500" /> High Impact
          </span>
          <span className="flex items-center gap-1 text-[10px] text-slate-500">
            <span className="w-2 h-2 rounded-full bg-amber-500" /> Medium
          </span>
          <span className="flex items-center gap-1 text-[10px] text-slate-500">
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> Low
          </span>
        </div>
        
        <p className="text-[10px] text-slate-600 text-center">
          Data from ForexFactory • Times shown in your local timezone
        </p>
      </div>
    </Card>
  );
}
